<template>
	<div class="flex">
		<DropdownStatus
			v-model="temporaryStatus"
 			:options="statusOptions"
			checkmark
			id="authoringStatus"
			optionLabel="title"
			optionValue="identifier"
      		:disabled="disabled"
			@change="switchStatus"
			placeholder="Select Status"
			:class="[
				'w-full border',
				selectedAuthoringStatus?.identifier === 'n' ? 'border-danger-500 focus:border-danger-500 active:border-danger-500 outline-danger-500'
				: selectedAuthoringStatus?.identifier === 'd' ? 'border-warning-500 focus:border-warning-500 active:border-warning-500 outline-warning-500'
				: selectedAuthoringStatus?.identifier === 'f' ? 'border-green-500 focus:border-green-500 active:border-green-500 outline-green-500'
				: ''
			]"
		>
			<template #value>
				<div class="flex items-center text-sm" :class="{ 
					'text-danger': selectedAuthoringStatus?.identifier === 'n', 
					'text-warning': selectedAuthoringStatus?.identifier === 'd', 
					'text-green-700': selectedAuthoringStatus?.identifier === 'f'
				}">
					<i
						class="w-5 h-5 mr-1"
						:class="{
							'tabler:info-circle color-danger-700': selectedAuthoringStatus?.identifier === 'n',
							'tabler:pencil-off color-warning': selectedAuthoringStatus?.identifier === 'd',
							'tabler:circle-check color-green': selectedAuthoringStatus?.identifier === 'f'
						}"
					></i>
					{{ selectedAuthoringStatus?.title }}
				</div>
			</template>

			<template #option="slotProps">
				<div class="flex items-center text-sm">
					<div>{{ slotProps.option.title }}</div>
				</div>
			</template>
		</DropdownStatus>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { AuthoringStatusList } from '@/types/cvTypes'
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import { useAppStore } from '@/stores/app-store';

const { currentLng } = useAppStore();

const emit = defineEmits<{
  authorStatusChange: [{ uid: string; newStatus: string }],
  // opsionale: nëse parent-i pret @statusChange
  statusChange: [string]
}>()

const props = withDefaults(defineProps<{
	jsonData: {
		identifier: string;
		authoringStatusCode: string;
		_links: { self: { href: string } };
	};
	statusOptions?: AuthoringStatusList[];
	disabled?: boolean;
	optionValue?: string;
	commitOnChange?: boolean

}>(), {
  disabled: false,
  optionValue: undefined
})

const temporaryStatus = ref<string>(props.jsonData.authoringStatusCode)
const currentStatus   = ref<string>(props.jsonData.authoringStatusCode)


const selectedAuthoringStatus = computed(() => {
  return props.statusOptions?.find(s => s.identifier === temporaryStatus.value) ?? { identifier: 'n', title: 'New' }
})


watch(() => props.optionValue, (v) => {
  if (v && temporaryStatus.value !== v) {
    temporaryStatus.value = v
    currentStatus.value   = v 
  }
})

watch(() => props.jsonData.authoringStatusCode, (v) => {
  if (v && temporaryStatus.value !== v) {
    temporaryStatus.value = v;
    currentStatus.value   = v;
  }
});

async function switchStatus(event: { value: string }) {
	const status = props.statusOptions?.find(item => item.identifier === event.value)
	const packageUID = props.jsonData.identifier

  temporaryStatus.value = event.value
  if (!status) return

  const patchStatus = async () => {
    try {
      const selfHref = props.jsonData?._links?.self?.href
      if (!selfHref) {
        throw new Error('Missing _links.self.href for authoring status update')
      }

      const url = new URL(selfHref, window.location.origin)
      if (currentLng.value) {
        url.searchParams.set('lng', String(currentLng.value))
      }

      const response = await fetch(url.toString(), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authoringStatusCode: status.identifier })
      })

      if (!response.ok) throw new Error(`Failed with status ${response.status}`)

      currentStatus.value   = status.identifier
      temporaryStatus.value = status.identifier

      emit('authorStatusChange', { uid: packageUID, newStatus: status.identifier })
      emit('statusChange', status.identifier) // opsionale

      await Swal.fire({
        title: 'Status Changed!',
        html: `Authoring status updated to <b>"<u>${status.title}</u>"</b>.`,
        icon: 'success',
        confirmButtonColor: '#15803d'
      })
    } catch (error) {
      console.error(error)
      await Swal.fire({
        title: 'Update Failed',
        text: 'An error occurred while updating the status.',
        icon: 'error',
        confirmButtonColor: '#d33'
      })
      // kthe dropdown-in në vlerën ekzistuese nëse PATCH dështoi
      temporaryStatus.value = currentStatus.value
    }
  }

  if (status.identifier === 'f') {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to change the authoring status of this item?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4679cc',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes'
    })

    if (result.isConfirmed) {
      await patchStatus()
    } else {
      temporaryStatus.value = currentStatus.value
    }
  } else {
    await patchStatus()
  }
}
</script>
<style>
#authoringStatus .p-inputtext {
	padding-left: 0.5rem !important;
	padding-right: 0 !important;
	padding-top: 0.5rem !important;
	padding-bottom: 0.5rem !important;
}
</style>
